import type { CalculatorKind } from '../types';

/**
 * Calculator framework.
 *
 * A calculator is a pure declaration: a list of input fields plus a `compute`
 * function. It holds no React, no DOM, and no formatting — which means every
 * calculator is trivially testable and can also run during prerender (e.g. to
 * render a worked example inside a case study) without pulling in a component.
 *
 * To add a calculator: write one `CalculatorDefinition` and register it in
 * `./index.ts`. The UI, routing, and prerendered page come for free.
 */

export type FieldKind =
  /** Whole dollars. Rendered with a $ affix; stored as a plain number. */
  | 'currency'
  /** Percentage points (7.5 means 7.5%). Rendered with a % affix. */
  | 'percent'
  | 'number'
  | 'months'
  | 'years'
  | 'select';

export type SelectOption = { value: string; label: string };

export type CalculatorField = {
  id: string;
  label: string;
  kind: FieldKind;
  /** Sits under the input. Explain the trap, not the obvious. */
  help?: string;
  defaultValue: number | string;
  min?: number;
  max?: number;
  step?: number;
  /** `select` only. */
  options?: SelectOption[];
  /** Groups fields under a subheading in the form. */
  group?: string;
  /**
   * Hide unless the predicate passes — keeps advanced knobs out of the way
   * until the choice that makes them relevant has been made.
   */
  visibleWhen?: (values: CalculatorValues) => boolean;
};

export type CalculatorValues = Record<string, number | string>;

export type OutputTone =
  | 'neutral'
  | 'currency' // good: money coming in
  | 'cost' // caution: money going out
  | 'risk'; // bad: loss, or a figure that should stop you

export type OutputFormat =
  | 'currency' // whole dollars
  | 'currency-exact' // dollars and cents
  | 'percent' // value is in percentage points
  | 'multiple' // 1.8×
  | 'months'
  | 'number'
  | 'number-1dp';

export type CalculatorOutput = {
  id: string;
  label: string;
  value: number;
  format: OutputFormat;
  tone?: OutputTone;
  /** Promotes the figure into the large headline row. Use for 1–3 outputs. */
  primary?: boolean;
  /** Short read of what the number means at its current value. */
  note?: string;
};

export type CalculatorResult = {
  outputs: CalculatorOutput[];
  /** Conditions worth surfacing above the results (assumption breaks, edge cases). */
  warnings?: string[];
};

export type CalculatorDefinition = {
  kind: CalculatorKind;
  slug: string;
  name: string;
  nameKo: string;
  /** One line, used on cards and the index. */
  summary: string;
  /** What the output means and — importantly — where it misleads. */
  interpretation: string;
  fields: CalculatorField[];
  compute: (values: CalculatorValues) => CalculatorResult;
  /** Rendered as fine print beneath the calculator. */
  disclaimer?: string;
  relatedCaseStudySlugs?: string[];
};

/* -------------------------------------------------------------------------- */
/*                             Value access helpers                            */
/* -------------------------------------------------------------------------- */

/** Coerce a field value to a finite number, falling back to 0. */
export function num(values: CalculatorValues, id: string, fallback = 0): number {
  const raw = values[id];
  const n = typeof raw === 'number' ? raw : Number.parseFloat(String(raw ?? ''));
  return Number.isFinite(n) ? n : fallback;
}

export function str(values: CalculatorValues, id: string, fallback = ''): string {
  const raw = values[id];
  return raw == null || raw === '' ? fallback : String(raw);
}

/** Percentage points → ratio. 7.5 → 0.075 */
export function pct(values: CalculatorValues, id: string, fallback = 0): number {
  return num(values, id, fallback) / 100;
}

/** Guards every division so a zero denominator yields null, never Infinity. */
export function safeDiv(numerator: number, denominator: number): number | null {
  if (!Number.isFinite(numerator) || !Number.isFinite(denominator)) return null;
  if (denominator === 0) return null;
  const result = numerator / denominator;
  return Number.isFinite(result) ? result : null;
}

export function defaultValuesFor(def: CalculatorDefinition): CalculatorValues {
  return Object.fromEntries(def.fields.map((f) => [f.id, f.defaultValue]));
}
